// components/ExperienceTimeline.jsx
import * as React from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { Typography, Box, useTheme, Paper, useMediaQuery } from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import { SiTcs } from "react-icons/si";
import { orange } from "../../theme/colors";

export default function ExperienceTimeline() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const experiences = [
    {
      date: "Aug 2024 – Present",
      title: "Assistant System Engineer",
      company: "Tata Consultancy Services",
      points: [
        "Developing and maintaining a full-stack enterprise dashboard using React, Node.js, Express, and PostgreSQL to support business operations.",
        "Implementing complex business logic to manage and track expenses, costs, revenue, and other operational metrics.",
        "Collaborating with cross-functional teams to deliver new features, resolve issues, and improve overall application performance.",
        "Working within Agile development workflows, contributing to requirement analysis, sprint planning, and code reviews.",
        "Writing clean, maintainable code while debugging issues to ensure application reliability, stability, and scalability.",
      ],
    },
    {
      date: "March 2024 – Aug 2024",
      title: "Backend Developer (Intern)",
      company: "Refast Technologies Pvt. Ltd.",
      points: [
        "Developed API endpoints for multiple features ensuring performance, scalability, and maintainability.",
        "Integrated third-party vendor APIs to extend application functionalities.",
        "Worked on mobile and web frontends using React Native and React.js, contributing to UI/UX improvements.",
        "Gained experience in end-to-end workflows: version control (Git), debugging, and deployment processes.",
      ],
    },
  ];

  return (
    <Box id="experience" sx={{ py: 8 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 6,
          fontFamily: "Poppins, sans-serif",
          textAlign: "center",
        }}
      >
        Experience
      </Typography>

      {/* Mobile: Cards only */}
      {isMobile ? (
        <Box>
          {experiences.map((exp, index) => (
            <Paper
              key={index}
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                mb: 3,
                mx: 3,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {exp.date}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {exp.title}
              </Typography>
              <Typography
                variant="subtitle1"
                color={orange[500]}
                sx={{ mb: 1 }}
              >
                {exp.company}
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {exp.points.map((point, i) => (
                  <li key={i}>
                    <Typography variant="body2">{point}</Typography>
                  </li>
                ))}
              </ul>
            </Paper>
          ))}
        </Box>
      ) : (
        // Desktop: Timeline on same side
        <Timeline position="right">
          {experiences.map((exp, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent
                sx={{ m: "auto 0", flex: 0.15 }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {exp.date}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color="primary">
                  {index === 0 ? (
                    <>
                      <SiTcs size={24} />
                    </>
                  ) : (
                    <WorkIcon />
                  )}
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: "12px", px: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {exp.title}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {exp.company}
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                    {exp.points.map((point, i) => (
                      <li key={i}>
                        <Typography variant="body2">{point}</Typography>
                      </li>
                    ))}
                  </ul>
                </Paper>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
}
